import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { ApiError, BoxOfficeVenueConfig, PrinterRow, RegisterRow } from '../api';
import { boxOfficeEndpoints, endpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table, formatWhen } from '../ui';
import { INPUT } from './event/shared';

/**
 * A venue's box office: registers (what a window is), card readers (Stripe
 * Terminal, simulated in test mode), ticket and receipt printers (bound to
 * a paired print bridge), and today's register sessions.
 */
export default function BoxOfficePage() {
  const { id = '' } = useParams();
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const venue = useQuery({ queryKey: ['venue', id], queryFn: () => endpoints.venue(id) });
  const config = useQuery<BoxOfficeVenueConfig, ApiError>({ queryKey: ['box-office', id], queryFn: () => boxOfficeEndpoints.venue(id) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['box-office', id] });
  const onError = (cause: ApiError) => setError(cause.message);

  const [register, setRegister] = useState({ code: '', name: '', allow_manual_card: false, blind_close: true });
  const createRegister = useMutation({ mutationFn: () => boxOfficeEndpoints.createRegister(id, register), onSuccess: () => { setRegister({ code: '', name: '', allow_manual_card: false, blind_close: true }); setError(null); refresh(); }, onError });
  const updateRegister = useMutation({ mutationFn: ({ registerId, input }: { registerId: string; input: Record<string, unknown> }) => boxOfficeEndpoints.updateRegister(registerId, input), onSuccess: refresh, onError });

  const [printer, setPrinter] = useState({ name: '', print_bridge_device_id: '', printer_model: 'BOCA_FGL' as PrinterRow['printer_model'], register_id: '', kind: 'tcp' as 'tcp' | 'usb' | 'windows', host: '', port: '9100', path: '/dev/usb/lp0', ticket_stock_id: '' });
  const createPrinter = useMutation({
    mutationFn: () => boxOfficeEndpoints.createPrinter(id, { name: printer.name, print_bridge_device_id: printer.print_bridge_device_id, printer_model: printer.printer_model, register_id: printer.register_id || null, ticket_stock_id: printer.ticket_stock_id || null, connection: printer.kind === 'tcp' ? { kind: 'tcp', host: printer.host, port: Number(printer.port) } : { kind: printer.kind, path: printer.path } }),
    onSuccess: () => { setPrinter({ ...printer, name: '', host: '' }); setError(null); refresh(); },
    onError,
  });
  const testPage = useMutation({ mutationFn: (printerId: string) => boxOfficeEndpoints.testPage(printerId), onSuccess: () => setNotice('Test page queued; the bridge prints it on its next poll.'), onError });

  const [terminal, setTerminal] = useState({ label: '', registration_code: '', register_id: '' });
  const registerTerminal = useMutation({ mutationFn: () => boxOfficeEndpoints.registerTerminal(id, { label: terminal.label, registration_code: terminal.registration_code, register_id: terminal.register_id || null }), onSuccess: () => { setTerminal({ label: '', registration_code: '', register_id: '' }); setError(null); refresh(); }, onError });
  const forceClose = useMutation({ mutationFn: (sessionId: string) => boxOfficeEndpoints.forceClose(sessionId, 'Force-closed from the admin'), onSuccess: refresh, onError });

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  if (config.error) return <ErrorNote>{config.error.message}</ErrorNote>;
  const data = config.data;
  const canManage = can('venues.manage');
  const canDevices = can('devices.manage');
  const registerName = (registerId: string | null) => data?.registers.find((r) => r.id === registerId)?.name ?? '—';

  return (
    <div className="space-y-4">
      <div className="flex items-baseline gap-3">
        <Link to={`/venues/${id}`} className="text-sm text-accent underline-offset-2 hover:underline">
          ← {venue.data?.venue.name ?? 'Venue'}
        </Link>
        <h1 className="text-lg font-semibold text-ink-900">Box office</h1>
      </div>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      {notice ? <p className="rounded border border-ok/30 bg-ok/10 px-3 py-2 text-sm text-ok">{notice}</p> : null}

      <Panel title="Registers" description="A register is a selling position. A box office terminal device is assigned to one (Devices), and opening it starts a session.">
        <Table headers={['Register', 'Defaults', 'Keyed cards', 'Blind close', '']}>
          {(data?.registers ?? []).length === 0 ? <EmptyRow columns={5}>No registers yet.</EmptyRow> : null}
          {(data?.registers ?? []).map((r: RegisterRow) => (
            <tr key={r.id} className="border-b border-line last:border-0">
              <td className="px-5 py-3">
                <div className="font-medium">{r.name}</div>
                <div className="font-mono text-xs text-ink-400">{r.code}</div>
              </td>
              <td className="px-5 py-3 text-xs text-ink-600">
                reader {data?.terminals.find((t) => t.id === r.default_payment_terminal_id)?.label ?? '—'} · tickets {data?.printers.find((p) => p.id === r.default_ticket_printer_id)?.name ?? '—'} · receipts {data?.printers.find((p) => p.id === r.default_receipt_printer_id)?.name ?? '—'}
              </td>
              <td className="px-5 py-3">
                <input type="checkbox" checked={r.allow_manual_card === 1} disabled={!canManage} onChange={(e) => updateRegister.mutate({ registerId: r.id, input: { allow_manual_card: e.target.checked } })} aria-label="Allow keyed cards" />
              </td>
              <td className="px-5 py-3">
                <input type="checkbox" checked={r.blind_close === 1} disabled={!canManage} onChange={(e) => updateRegister.mutate({ registerId: r.id, input: { blind_close: e.target.checked } })} aria-label="Blind close" />
              </td>
              <td className="px-5 py-3 text-right">
                <Badge tone={r.active ? 'ok' : 'neutral'}>{r.active ? 'active' : 'inactive'}</Badge>
              </td>
            </tr>
          ))}
        </Table>
        {canManage ? (
          <form className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4" onSubmit={(e) => { e.preventDefault(); createRegister.mutate(); }}>
            <Field label="Code" htmlFor="reg-code"><input id="reg-code" className={INPUT} value={register.code} onChange={(e) => setRegister({ ...register, code: e.target.value })} placeholder="W1" /></Field>
            <Field label="Name" htmlFor="reg-name"><input id="reg-name" className={INPUT} value={register.name} onChange={(e) => setRegister({ ...register, name: e.target.value })} placeholder="Window 1" /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={register.blind_close} onChange={(e) => setRegister({ ...register, blind_close: e.target.checked })} /> Blind close</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={register.allow_manual_card} onChange={(e) => setRegister({ ...register, allow_manual_card: e.target.checked })} /> Keyed cards</label>
            <Button type="submit" variant="primary" disabled={!register.code || !register.name || createRegister.isPending}>Add register</Button>
          </form>
        ) : null}
      </Panel>

      <Panel title="Card readers" description="Stripe Terminal readers in the Ticketing account. Enter the code the reader shows on its screen; in test mode, simulated-wpe makes a simulated reader.">
        <Table headers={['Reader', 'Stripe', 'Register', 'Status', '']}>
          {(data?.terminals ?? []).length === 0 ? <EmptyRow columns={5}>No readers registered.</EmptyRow> : null}
          {(data?.terminals ?? []).map((t) => (
            <tr key={t.id} className="border-b border-line last:border-0">
              <td className="px-5 py-3 font-medium">{t.label}{t.simulated ? <Badge tone="warn">simulated</Badge> : null}</td>
              <td className="px-5 py-3 font-mono text-xs text-ink-400">{t.stripe_reader_id} · {t.device_model ?? ''}</td>
              <td className="px-5 py-3 text-sm">{registerName(t.register_id)}</td>
              <td className="px-5 py-3 text-xs text-ink-600">{t.last_known_status ?? '—'} · {formatWhen(t.last_seen_at)}</td>
              <td className="px-5 py-3 text-right"><Badge tone={t.active ? 'ok' : 'neutral'}>{t.active ? 'active' : 'inactive'}</Badge></td>
            </tr>
          ))}
        </Table>
        {can('settings.manage') ? (
          <form className="flex flex-wrap items-end gap-3 border-t border-line px-5 py-4" onSubmit={(e) => { e.preventDefault(); registerTerminal.mutate(); }}>
            <Field label="Label" htmlFor="term-label"><input id="term-label" className={INPUT} value={terminal.label} onChange={(e) => setTerminal({ ...terminal, label: e.target.value })} placeholder="Window 1 reader" /></Field>
            <Field label="Registration code" htmlFor="term-code" hint="From the reader's screen, or simulated-wpe in test mode"><input id="term-code" className={INPUT} value={terminal.registration_code} onChange={(e) => setTerminal({ ...terminal, registration_code: e.target.value })} /></Field>
            <Field label="Register" htmlFor="term-register">
              <select id="term-register" className={INPUT} value={terminal.register_id} onChange={(e) => setTerminal({ ...terminal, register_id: e.target.value })}>
                <option value="">—</option>
                {(data?.registers ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
            <Button type="submit" variant="primary" disabled={!terminal.label || !terminal.registration_code || registerTerminal.isPending}>Register reader</Button>
          </form>
        ) : null}
      </Panel>

      <Panel title="Printers" description="Each printer is reached through a paired print bridge on the venue network (Devices → PRINT_BRIDGE). BOCA printers take tickets; ESC/POS or Star printers take receipts.">
        <Table headers={['Printer', 'Bridge', 'Connection', 'Register', 'Status', '']}>
          {(data?.printers ?? []).length === 0 ? <EmptyRow columns={6}>No printers yet.</EmptyRow> : null}
          {(data?.printers ?? []).map((p) => (
            <tr key={p.id} className="border-b border-line last:border-0">
              <td className="px-5 py-3"><div className="font-medium">{p.name}</div><div className="text-xs text-ink-400">{p.printer_model.replaceAll('_', ' ')}</div></td>
              <td className="px-5 py-3 text-sm">{p.bridge_name}<div className="text-xs text-ink-400">seen {formatWhen(p.bridge_last_seen_at)}</div></td>
              <td className="px-5 py-3 font-mono text-xs text-ink-600">{p.connection.kind === 'tcp' ? `${p.connection.host}:${p.connection.port ?? 9100}` : p.connection.path}</td>
              <td className="px-5 py-3 text-sm">{registerName(p.register_id)}</td>
              <td className="px-5 py-3 text-xs text-ink-600">{p.last_status ?? '—'} · {formatWhen(p.last_status_at)}</td>
              <td className="px-5 py-3 text-right whitespace-nowrap">
                {canDevices ? <Button disabled={testPage.isPending} onClick={() => testPage.mutate(p.id)}>Test page</Button> : null}
              </td>
            </tr>
          ))}
        </Table>
        {canDevices ? (
          <form className="grid gap-3 border-t border-line px-5 py-4 md:grid-cols-4" onSubmit={(e) => { e.preventDefault(); createPrinter.mutate(); }}>
            <Field label="Name" htmlFor="pr-name"><input id="pr-name" className={`${INPUT} w-full`} value={printer.name} onChange={(e) => setPrinter({ ...printer, name: e.target.value })} placeholder="BOCA window 1" /></Field>
            <Field label="Bridge" htmlFor="pr-bridge">
              <select id="pr-bridge" className={`${INPUT} w-full`} value={printer.print_bridge_device_id} onChange={(e) => setPrinter({ ...printer, print_bridge_device_id: e.target.value })}>
                <option value="">—</option>
                {(data?.bridges ?? []).map((b) => <option key={b.id} value={b.id}>{b.name} ({b.status.toLowerCase().replaceAll('_', ' ')})</option>)}
              </select>
            </Field>
            <Field label="Model" htmlFor="pr-model">
              <select id="pr-model" className={`${INPUT} w-full`} value={printer.printer_model} onChange={(e) => setPrinter({ ...printer, printer_model: e.target.value as PrinterRow['printer_model'] })}>
                <option value="BOCA_FGL">BOCA (FGL) tickets</option>
                <option value="EPSON_ESCPOS">Epson ESC/POS receipts</option>
                <option value="STAR_LINE">Star receipts</option>
              </select>
            </Field>
            <Field label="Register" htmlFor="pr-register">
              <select id="pr-register" className={`${INPUT} w-full`} value={printer.register_id} onChange={(e) => setPrinter({ ...printer, register_id: e.target.value })}>
                <option value="">—</option>
                {(data?.registers ?? []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
            <Field label="Connection" htmlFor="pr-kind">
              <select
                id="pr-kind"
                className={`${INPUT} w-full`}
                value={printer.kind}
                onChange={(e) => {
                  const kind = e.target.value as 'tcp' | 'usb' | 'windows';
                  // The placeholder has to follow the kind: a Linux device path
                  // in a Windows printer-name box is a silent mis-config.
                  setPrinter({ ...printer, kind, path: kind === 'windows' ? '' : printer.path || '/dev/usb/lp0' });
                }}
              >
                <option value="tcp">Network (TCP 9100)</option>
                <option value="usb">USB on a Linux bridge</option>
                <option value="windows">USB on a Windows bridge</option>
              </select>
            </Field>
            {printer.kind === 'tcp' ? (
              <>
                <Field label="Host" htmlFor="pr-host"><input id="pr-host" className={`${INPUT} w-full`} value={printer.host} onChange={(e) => setPrinter({ ...printer, host: e.target.value })} placeholder="10.0.0.20" /></Field>
                <Field label="Port" htmlFor="pr-port"><input id="pr-port" className={`${INPUT} w-full`} value={printer.port} onChange={(e) => setPrinter({ ...printer, port: e.target.value })} /></Field>
              </>
            ) : (
              <Field label={printer.kind === 'windows' ? 'Windows printer name' : 'Device path'} htmlFor="pr-path">
                <input
                  id="pr-path"
                  className={`${INPUT} w-full`}
                  value={printer.path}
                  onChange={(e) => setPrinter({ ...printer, path: e.target.value })}
                  placeholder={printer.kind === 'windows' ? 'Boca BIDI FGL 26/46 300 DPI' : '/dev/usb/lp0'}
                />
                {printer.kind === 'windows' ? <p className="mt-1 text-xs text-ink-500">Exactly as Windows spells it. Run the bridge with --list-printers on that machine to copy it.</p> : null}
              </Field>
            )}
            {printer.printer_model === 'BOCA_FGL' ? (
              <Field label="Ticket stock" htmlFor="pr-stock">
                <select id="pr-stock" className={`${INPUT} w-full`} value={printer.ticket_stock_id} onChange={(e) => setPrinter({ ...printer, ticket_stock_id: e.target.value })}>
                  <option value="">—</option>
                  {(data?.stocks ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
            ) : null}
            <div className="flex items-end"><Button type="submit" variant="primary" disabled={!printer.name || !printer.print_bridge_device_id || createPrinter.isPending}>Add printer</Button></div>
          </form>
        ) : null}
      </Panel>

      <Panel title="Register sessions" description="Open and recent sessions. Force-closing a session with drawers still open leaves those drawers to be reconciled from Drawer shifts.">
        <Table headers={['Register', 'Device', 'Opened', 'Closed', 'Drawers open', 'Status', '']}>
          {(data?.sessions ?? []).length === 0 ? <EmptyRow columns={7}>No sessions yet.</EmptyRow> : null}
          {(data?.sessions ?? []).map((s) => (
            <tr key={s.id} className="border-b border-line last:border-0">
              <td className="px-5 py-3 font-medium">{s.register_name}</td>
              <td className="px-5 py-3 text-sm text-ink-600">{s.device_name}</td>
              <td className="px-5 py-3 text-xs text-ink-600">{formatWhen(s.opened_at)} · {s.opened_by_name}</td>
              <td className="px-5 py-3 text-xs text-ink-600">{formatWhen(s.closed_at)}</td>
              <td className="tabular px-5 py-3 text-sm">{s.open_shifts}</td>
              <td className="px-5 py-3"><Badge tone={s.status === 'OPEN' ? 'ok' : s.status === 'FORCE_CLOSED' ? 'warn' : 'neutral'}>{s.status.replaceAll('_', ' ')}</Badge></td>
              <td className="px-5 py-3 text-right">{s.status === 'OPEN' && can('box_office.session.force_close') ? <Button variant="danger" onClick={() => window.confirm('Force-close this register session?') && forceClose.mutate(s.id)}>Force close</Button> : null}</td>
            </tr>
          ))}
        </Table>
      </Panel>
    </div>
  );
}
